import { HardwareSupplierFilterKeyType } from 'opensolar-common'
import { SLOT_AUTO_SELECT_NOTE_KEY } from './constants'

export type IsolatorSelectionNote =
  | { type: 'phase_escalation'; supplierKey?: string }
  | { type: 'size_escalation'; supplierKey?: string; currentRating: number }

export const ISOLATOR_PHASE_ESCALATION_MESSAGE_KEY =
  'A three-phase isolator was selected because %{supplierName} does not stock an appropriate single-phase isolator for this inverter.'

export const ISOLATOR_SIZE_ESCALATION_MESSAGE_KEY =
  'A %{currentRating}A isolator was selected because %{supplierName} does not stock a smaller single-phase isolator with enough headroom for this inverter.'

export const ISOLATOR_PREFERRED_SUPPLIER_FALLBACK_KEY = 'your preferred supplier'

export function buildPhaseEscalationNote(supplierKey?: string): IsolatorSelectionNote {
  return { type: 'phase_escalation', supplierKey }
}

export function buildSizeEscalationNote(currentRating: number, supplierKey?: string): IsolatorSelectionNote {
  return { type: 'size_escalation', supplierKey, currentRating }
}

export function parseIsolatorSelectionNote(value: unknown): IsolatorSelectionNote | null {
  if (!value || typeof value !== 'object') return null
  const note = value as IsolatorSelectionNote
  if (note.type === 'phase_escalation') return { type: 'phase_escalation', supplierKey: note.supplierKey }
  if (note.type === 'size_escalation' && Number.isFinite(Number(note.currentRating))) {
    return { type: 'size_escalation', supplierKey: note.supplierKey, currentRating: Number(note.currentRating) }
  }
  return null
}

export function readIsolatorSelectionNoteFromSlot(
  properties?: Record<string, unknown>
): IsolatorSelectionNote | null {
  if (!properties) return null
  return parseIsolatorSelectionNote(properties[SLOT_AUTO_SELECT_NOTE_KEY])
}

export type IsolatorSelectionNoteTranslationParams = {
  key: string
  options: { supplierName: string; currentRating?: number }
}

export function getIsolatorSelectionNoteTranslationParams(
  note: IsolatorSelectionNote,
  supplierDisplayName: string
): IsolatorSelectionNoteTranslationParams {
  if (note.type === 'phase_escalation') {
    return {
      key: ISOLATOR_PHASE_ESCALATION_MESSAGE_KEY,
      options: { supplierName: supplierDisplayName },
    }
  }

  return {
    key: ISOLATOR_SIZE_ESCALATION_MESSAGE_KEY,
    options: { supplierName: supplierDisplayName, currentRating: note.currentRating },
  }
}

export function resolveSupplierDisplayName(
  supplierKey: string | undefined,
  getSupplierConfig: (hardwareSupplier: HardwareSupplierFilterKeyType) => { name: string } | null
): string {
  if (!supplierKey) return ISOLATOR_PREFERRED_SUPPLIER_FALLBACK_KEY
  return getSupplierConfig(supplierKey as HardwareSupplierFilterKeyType)?.name ?? supplierKey
}
